import { useMemo, useState, useEffect } from "react";
import { Link, useParams, useSearchParams, useNavigate } from "react-router-dom";
import { Search, SlidersHorizontal, X, ChevronRight } from "lucide-react";
import { useFetch } from "@/hooks/useFetch";
import { Seo } from "@/components/site/Seo";
import { Breadcrumbs } from "@/components/site/PageHero";
import { ProductCard } from "@/components/site/ProductCard";
import { SectionLabel } from "@/components/site/Img";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";

export function buildTree(cats) {
  const byParent = {};
  (cats || []).forEach((c) => { (byParent[c.parent_id || "root"] ||= []).push(c); });
  const total = (c) => c.product_count + (byParent[c.id] || []).reduce((s, k) => s + total(k), 0);
  const walk = (pid, depth) => (byParent[pid] || []).sort((a, b) => a.sort_order - b.sort_order).map((c) => ({ ...c, depth, total: total(c), children: walk(c.id, depth + 1) }));
  return walk("root", 0);
}

function renderNodes(nodes, active, onPick) {
  return nodes.map((node) => {
    const isActive = node.slug === active;
    return (
      <li key={node.id}>
        <button onClick={() => onPick(node.slug)} data-testid={`category-tree-item-${node.slug}`}
          className={`flex w-full items-center justify-between py-2 text-left text-sm transition-colors ${isActive ? "text-[color:var(--ice)]" : node.depth === 0 ? "text-slate-100 hover:text-white" : "text-slate-400 hover:text-white"}`}
          style={{ paddingLeft: node.depth * 14 }}>
          <span className="flex items-center gap-2">
            {node.depth > 0 && <span className={`h-1.5 w-1.5 rotate-45 border ${isActive ? "border-[color:var(--ice)] bg-[color:var(--ice)]" : "border-slate-600"}`} />}
            <span className={node.depth === 0 ? "font-display text-base uppercase tracking-wide" : ""}>{node.name}</span>
          </span>
          <span className="font-mono text-[10px] text-slate-600">{node.total}</span>
        </button>
        {node.children.length > 0 && <ul>{renderNodes(node.children, active, onPick)}</ul>}
      </li>
    );
  });
}

function CategoryTree({ tree, active, onPick }) {
  return (
    <nav aria-label="Categories" data-testid="category-tree">
      <button onClick={() => onPick(null)} data-testid="category-tree-item-all"
        className={`mb-2 w-full py-2 text-left font-display text-base uppercase tracking-wide ${!active ? "text-[color:var(--ice)]" : "text-slate-100 hover:text-white"}`}>All products</button>
      <ul className="space-y-1 divide-y divide-white/[0.04]">{renderNodes(tree, active, onPick)}</ul>
    </nav>
  );
}

export default function Shop() {
  const { slug } = useParams();
  const [sp, setSp] = useSearchParams();
  const nav = useNavigate();
  const q = sp.get("q") || "";
  const sort = sp.get("sort") || "default";
  const inStock = sp.get("in_stock") === "1";
  const [term, setTerm] = useState(q);
  const [filtersOpen, setFiltersOpen] = useState(false);
  useEffect(() => setTerm(q), [q]);
  const { data: cats } = useFetch("/categories");
  const { data: page } = useFetch("/pages/shop");
  const { data: products, loading, error } = useFetch("/products", { category: slug || undefined, q: q || undefined, sort, in_stock: inStock || undefined });
  const tree = useMemo(() => buildTree(cats), [cats]);
  const current = (cats || []).find((c) => c.slug === slug);
  const parent = current && (cats || []).find((c) => c.id === current.parent_id);
  const subcats = current ? (cats || []).filter((c) => c.parent_id === current.id) : [];
  const pick = (s) => { setFiltersOpen(false); nav(s ? `/shop/category/${s}${sp.toString() ? "?" + sp : ""}` : `/shop${sp.toString() ? "?" + sp : ""}`); };
  const setParam = (k, v) => { const n = new URLSearchParams(sp); v ? n.set(k, v) : n.delete(k); setSp(n); };
  const path = slug ? `/shop/category/${slug}` : "/shop";
  const crumbs = [["/", "Home"], ["/shop", "Shop"]];
  if (parent) crumbs.push([`/shop/category/${parent.slug}`, parent.name]);
  if (current) crumbs.push([null, current.name]);

  return (
    <div data-testid="shop-page">
      <Seo path={path} />
      <section className="border-b border-[color:var(--line)] pb-10 pt-10">
        <div className="container-x">
          <Breadcrumbs items={crumbs} />
          <SectionLabel rune="ᛊ">{current ? "Category" : page?.eyebrow || "Shop"}</SectionLabel>
          <h1 className="mt-4 font-display text-4xl font-bold uppercase leading-none sm:text-5xl lg:text-6xl" data-testid="page-h1">{current ? current.name : page?.h1 || "Shop"}</h1>
          <p className="mt-4 max-w-2xl text-slate-400">{current ? current.description : page?.intro}</p>
          {subcats.length > 0 && (
            <div className="mt-6 flex flex-wrap gap-2">
              {subcats.map((c) => <button key={c.id} onClick={() => pick(c.slug)} className="btn-ghost !px-4 !py-2 !text-xs" data-testid={`subcategory-chip-${c.slug}`}>{c.name}<ChevronRight className="h-3 w-3" /></button>)}
            </div>
          )}
        </div>
      </section>
      <div className="container-x grid gap-10 py-10 lg:grid-cols-[260px_1fr]">
        <aside className="hidden lg:block"><div className="sticky top-28"><CategoryTree tree={tree} active={slug} onPick={pick} /></div></aside>
        <div>
          <div className="mb-8 flex flex-col gap-3 sm:flex-row sm:items-center">
            <form onSubmit={(e) => { e.preventDefault(); setParam("q", term.trim()); }} className="relative flex-1" role="search">
              <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
              <input value={term} onChange={(e) => setTerm(e.target.value)} placeholder="Search tools, ECUs, part numbers…" className="field !pl-11" aria-label="Search products" data-testid="shop-search-input" />
              {q && <button type="button" onClick={() => { setTerm(""); setParam("q", ""); }} className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-slate-400" aria-label="Clear search" data-testid="shop-search-clear"><X className="h-4 w-4" /></button>}
            </form>
            <div className="flex gap-3">
              <button onClick={() => setFiltersOpen(true)} className="btn-ghost !px-4 lg:hidden" data-testid="shop-filters-button"><SlidersHorizontal className="h-4 w-4" />Categories</button>
              <label className="flex items-center gap-2 whitespace-nowrap border border-[color:var(--line-2)] px-4 text-sm text-slate-300">
                <input type="checkbox" checked={inStock} onChange={(e) => setParam("in_stock", e.target.checked ? "1" : "")} className="accent-[#7dd3fc]" data-testid="shop-instock-filter" />In stock
              </label>
              <select value={sort} onChange={(e) => setParam("sort", e.target.value === "default" ? "" : e.target.value)} className="field !w-auto" aria-label="Sort" data-testid="shop-sort-select">
                <option value="default">Featured</option><option value="price_asc">Price: low–high</option><option value="price_desc">Price: high–low</option><option value="name">Name</option>
              </select>
            </div>
          </div>
          <p className="mb-5 font-mono text-[11px] uppercase tracking-[0.18em] text-slate-500" data-testid="shop-result-count">{loading ? "Loading…" : `${products?.length || 0} products${q ? ` for “${q}”` : ""}`}</p>
          {error && <p className="text-red-400">Couldn't load products. Please refresh.</p>}
          <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3" data-testid="shop-product-grid">
            {(products || []).map((p) => <ProductCard key={p.id} p={p} />)}
          </div>
          {products && products.length === 0 && (
            <div className="panel p-10 text-center text-slate-400" data-testid="shop-empty">No products match. <Link to="/contact" className="text-[color:var(--ice)] underline">Ask us</Link> — we supply more than we list.</div>
          )}
        </div>
      </div>
      <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
        <SheetContent side="left" className="w-[85%] overflow-y-auto border-r border-[color:var(--line)] bg-[#0d1014]" data-testid="shop-filters-sheet">
          <SheetHeader><SheetTitle className="font-display uppercase">Categories</SheetTitle></SheetHeader>
          <div className="mt-6"><CategoryTree tree={tree} active={slug} onPick={pick} /></div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
